import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr 2fr;
            grid-template-rows: auto auto 1fr;
            grid-template-areas:
                "title title"
                "header content"
                "footer content";
            grid-column-gap: 0px;
            grid-row-gap: 0px;
            margin: 0 auto;
        }

        .title {
            text-align: center;
            margin-top: 10px;
            grid-area: title;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            grid-area: header;
            margin-bottom: 50px;
        }

        .header img {
            width: 100%;
            height: auto;
            image-rendering: pixelated;
            max-width: 40em;
            max-height: 25em;
            min-height: 25em;
            min-width: 40em;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            grid-area: content;
            padding: 10px;
            border: 1px solid #ffffff;
            width: 500px; // Verander deze waarde naar de gewenste breedte
            height: 500px; // Verander deze waarde naar de gewenste hoogte
            overflow: auto;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background-color: #ffffff;
            border: 1px solid #332c57;
            margin-top: 10px;
            display: flex;
            height: 105px;
            grid-area: footer;
            margin-bottom: 10px;
        }

        .footer .button {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
            font-size: large;
        }

        .footer .button {
            background-color: #000000;
            color: #ff0000;
            border: 1px solid #332c57;
            border-radius: 10px;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            margin-top: 20px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #ffffff;
        }

        .parent {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh; // Dit zorgt ervoor dat de ouder de volledige hoogte van het scherm inneemt
        }
        @media (max-width: 1760px) {
            .header img {
                width: 100%;
                height: auto;
                image-rendering: pixelated;
                max-width: 35em;
                max-height: 21em;
                min-height: 21em;
                min-width: 35em;
            }
            .footer .button {
                font-size: medium;
                margin: 1em 0 0.5em 0.3em;
            }
            .content {
                grid-area: content;
                padding: 10px;
                border: 1px solid #ffffff;
                font-size: large;

                max-height: 69.5%;
                overflow: auto;
            }
        }
    `;

    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            Array.from(this.selectedGameObjectButtons, (e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        if (state === undefined) {
            return;
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        return html`<div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    ${this.actionButtons?.map(
                        (button) => html`<a
                            class="button ${this.selectedActionButton === button ? "active" : ""}"
                            @click=${(): void => void this.handleClickAction(button)}
                            >${button.label}</a
                        >`
                    )}
                </div>
            </div>
            ${this.selectedActionButton ? this.renderActionButtons() : nothing}
        `;
    }
    private renderActionButtons(): TemplateResult {
        return html`
            <div class="footer">
                <div class="ObjectButtons">
                    <div class="ObjectButton ${this.selectedActionButton ? "" : ""}">
                        ${this.gameObjectButtons
                            ?.filter((button) => button.actions.includes(this.selectedActionButton!.alias))
                            .map(
                                (button) => html`<a
                                    class="button ${this.selectedGameObjectButtons.has(button)
                                        ? "active"
                                        : ""}"
                                    @click=${(): void => void this.handleClickObject(button)}
                                    >${button.name}</a
                                >`
                            )}
                    </div>
                </div>
            </div>
        `;
    }
}
